import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import transformContentHtml from "@insite/client-framework/Common/Utilities/transformContentHtml";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Img from "@insite/mobius/Img";
import LazyImage from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries, getProp } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { TYPOGRAPHY } from "@tCentro/theme/design";
import parse from "html-react-parser";
import React, { FC } from "react";
import { css } from "styled-components";

interface CardModel {
    fields: {
        cardTitle: string;
        cardDescription: string;
        cardButtonLink: LinkFieldValue;
        cardButtonLabel: string;
    };
}

const enum fields {
    cards = "cards",
    cardTitle = "cardTitle",
    cardDescription = "cardDescription",
    cardButtonLink = "cardButtonLink",
    cardButtonLabel = "cardButtonLabel",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.cards]: CardModel[];
    };
}

export interface CENContactCardStyles {
    cardParentContainer?: InjectableCss;
    cardChildContainer?: InjectableCss;
    cardDescriptionStyles?: TypographyProps;
    cardTitleStyles?: TypographyProps;
    buttonStyles?: LinkPresentationProps;
}

export const cenContactCardStyles: CENContactCardStyles = {
    cardParentContainer: {
        css: css`
            width: 100%;
            display: flex;
            gap: 24px;
            flex-wrap: wrap;
            justify-content: flex-start;
            margin-top: 56px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-direction: column;
                        align-items: center;
                    `,
                    css`
                        flex-direction: column;
                        align-items: flex-start;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    cardChildContainer: {
        css: css`
            border: 4px solid var(--neutrals-not-white, #f2f2f2);
            background-color: #fff;
            width: 31%;
            display: flex;
            flex-direction: column;
            justify-content: space-between;
            padding: 32px;
            box-shadow: 0px 4px 8px 0px rgba(38, 37, 37, 0.24);
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        width: 100%;
                    `,
                    css`
                        width: 95%;
                    `,
                    css`
                        width: 45%;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    cardTitleStyles: {
        css: css`
            font-size: 16px;
            font-style: normal;
            font-weight: 600;
            color: ${getProp("theme.typography.p.color")};
            line-height: 24px;
        `,
    },
    cardDescriptionStyles: {
        css: css`
            p {
                font-size: 12px;
                font-style: normal;
                font-weight: 400;
                line-height: 16px;
                margin-top: 8px;
            }
        `,
    },
    buttonStyles: {
        color: "#FFF",
        css: css`
            background-color: #8f0446;
            padding: 8px 16px;
            width: max-content;
            margin-top: 24px;
            border-radius: 16px;
            > span {
                font-size: 14px;
                font-weight: 600;
                line-height: 16px;
            }
        `,
    },
};

const styles = cenContactCardStyles;

export const CENContactUsCards: FC<OwnProps> = ({ fields }) => {
    return (
        <StyledWrapper {...styles.cardParentContainer}>
            {fields.cards.map(item => {
                const { url } = useGetLink(item.fields.cardButtonLink);
                return (
                    <>
                        <StyledWrapper {...styles.cardChildContainer}>
                            <Typography as="p" {...styles.cardTitleStyles}>
                                {item.fields.cardTitle}
                            </Typography>
                            {item.fields.cardDescription && (
                                <Typography as="p" {...styles.cardDescriptionStyles}>
                                    {parse(transformContentHtml(item.fields.cardDescription), parserOptions)}
                                </Typography>
                            )}
                            {item.fields.cardButtonLabel.length > 0 && (
                                <Link href={url} {...styles.buttonStyles}>
                                    {item.fields.cardButtonLabel}
                                </Link>
                            )}
                        </StyledWrapper>
                    </>
                );
            })}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: CENContactUsCards,
    definition: {
        group: "Basic",
        displayName: "CEN ContactUsCards",
        fieldDefinitions: [
            {
                name: fields.cards,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields) => {
                    return item.fields.cardTitle;
                },
                defaultValue: [],
                fieldType: "General",
                displayName: "Add cards",
                fieldDefinitions: [
                    {
                        name: fields.cardTitle,
                        editorTemplate: "TextField",
                        displayName: "Title",
                        defaultValue: "",
                    },
                    {
                        name: fields.cardDescription,
                        editorTemplate: "RichTextField",
                        defaultValue: "",
                    },
                    {
                        name: fields.cardButtonLabel,
                        editorTemplate: "TextField",
                        displayName: "Button Label",
                        defaultValue: "",
                    },
                    {
                        name: "cardButtonLink",
                        displayName: "Redirection Link",
                        editorTemplate: "LinkField",
                        defaultValue: { type: "Page", value: "" },
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
